/** One message on an event board: avatar, name, section badge, time, "breytt", reply quote, actions. */
import { CloudOff, CornerDownRight, Pencil, Reply, RotateCcw, Trash2, TriangleAlert, type LucideIcon } from 'lucide-react';
import { useState } from 'react';
import type { BoardComment } from '../../api/calendar';
import { Avatar, SectionBadge } from '../../components/people';
import { Button, cx, TextArea } from '../../components/ui';
import { useI18n, useT } from '../../i18n';
import { formatRelativeTime } from '../../lib/dates';
import { Linkified } from './shared';

export const COMMENT_MAX = 2000;

function excerpt(s: string, n = 140): string {
  const one = s.replace(/\s+/g, ' ').trim();
  return one.length > n ? `${one.slice(0, n - 1)}…` : one;
}

function ActionButton({ icon: Icon, label, onClick, danger }: { icon: LucideIcon; label: string; onClick: () => void; danger?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        'inline-flex min-h-11 items-center gap-1.5 rounded-xl px-2.5 text-[15px] font-semibold transition hover:bg-[var(--accent-soft)]',
        danger ? 'text-[var(--danger-text)]' : 'text-[var(--muted)]',
      )}
    >
      <Icon className="size-4.5" aria-hidden />
      {label}
    </button>
  );
}

export function CommentItem({
  c,
  parent,
  mine,
  canDelete,
  now,
  isNew,
  onReply,
  onEdit,
  onDelete,
  onRetry,
  onDiscard,
}: {
  c: BoardComment;
  parent: BoardComment | null;
  mine: boolean;
  canDelete: boolean;
  now: number;
  isNew: boolean;
  onReply: (c: BoardComment) => void;
  onEdit: (id: string, body: string) => Promise<boolean>;
  onDelete: (c: BoardComment) => void;
  onRetry: (c: BoardComment) => void;
  onDiscard: (c: BoardComment) => void;
}) {
  const t = useT();
  const { loc } = useI18n();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  if (c.body === null) {
    return (
      <li className="flex items-center gap-3 py-2 pl-1 text-[15px] text-[var(--muted)] italic">
        <Trash2 className="size-4" aria-hidden />
        {t('calendar.board.deleted')}
      </li>
    );
  }

  const a = c.author;
  const unsent = c.local === 'queued' || c.local === 'failed';

  const save = async () => {
    const body = draft.trim();
    if (!body) return;
    if (body === c.body) {
      setEditing(false);
      return;
    }
    setSaving(true);
    const ok = await onEdit(c.id, body);
    setSaving(false);
    if (ok) setEditing(false);
  };

  return (
    <li className={cx('flex gap-3 py-3', isNew && '-mx-2 rounded-2xl bg-[var(--accent-soft)] px-2')}>
      <Avatar userId={a.id} name={a.fullName} rev={a.avatarRev} section={a.section} size={44} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className={cx('font-semibold', a.inactive && 'text-[var(--muted)]')}>
            {a.fullName}
            {a.inactive ? ` ${t('calendar.board.left')}` : ''}
          </span>
          <SectionBadge section={a.section} title={a.title} short />
          <span className="text-[14px] text-[var(--muted)]">
            {formatRelativeTime(c.createdAt, now, loc)}
            {c.editedAt ? ` · ${t('calendar.board.edited')}` : ''}
          </span>
          {isNew ? <span className="rounded-full bg-[var(--danger)] px-2 py-0.5 text-[12px] font-bold text-white">{t('calendar.board.new')}</span> : null}
        </div>

        {c.replyTo ? (
          <div className="mt-1 flex items-start gap-1.5 border-l-4 border-[var(--line)] py-0.5 pl-2 text-[15px] text-[var(--muted)]">
            <CornerDownRight className="mt-0.5 size-4 shrink-0" aria-hidden />
            <span className="min-w-0">
              {parent && parent.body !== null ? (
                <>
                  <span className="font-semibold">{parent.author.fullName}: </span>
                  {excerpt(parent.body)}
                </>
              ) : (
                <span className="italic">{t('calendar.board.deleted')}</span>
              )}
            </span>
          </div>
        ) : null}

        {editing ? (
          <div className="mt-2 flex flex-col gap-2">
            <TextArea value={draft} maxLength={COMMENT_MAX} rows={3} onChange={(e) => setDraft(e.target.value)} autoFocus aria-label={t('common.edit')} />
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" className="min-h-11" onClick={() => setEditing(false)}>
                {t('common.cancel')}
              </Button>
              <Button size="sm" className="min-h-11" busy={saving} disabled={!draft.trim()} onClick={() => void save()}>
                {t('common.save')}
              </Button>
            </div>
          </div>
        ) : (
          <p className={cx('mt-0.5 text-[17px] break-words whitespace-pre-wrap', c.local === 'sending' && 'opacity-70')}>
            <Linkified text={c.body} />
          </p>
        )}

        {c.local === 'sending' ? <p className="text-[14px] text-[var(--muted)]">{t('calendar.board.sending')}</p> : null}
        {unsent ? (
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <span className={cx('inline-flex items-center gap-1.5 text-[14px] font-semibold', c.local === 'failed' ? 'text-[var(--danger-text)]' : 'text-[var(--ink-2)]')}>
              {c.local === 'failed' ? <TriangleAlert className="size-4" aria-hidden /> : <CloudOff className="size-4 text-[var(--warn-text)]" aria-hidden />}
              {c.local === 'failed' ? t('calendar.board.failed') : t('calendar.board.queued')}
            </span>
            <ActionButton icon={RotateCcw} label={t('common.retry')} onClick={() => onRetry(c)} />
            <ActionButton icon={Trash2} label={t('calendar.board.discard')} onClick={() => onDiscard(c)} danger />
          </div>
        ) : null}

        {!editing && !c.local ? (
          <div className="-ml-2.5 flex flex-wrap items-center">
            <ActionButton icon={Reply} label={t('calendar.board.reply')} onClick={() => onReply(c)} />
            {mine ? (
              <ActionButton
                icon={Pencil}
                label={t('common.edit')}
                onClick={() => {
                  setDraft(c.body ?? '');
                  setEditing(true);
                }}
              />
            ) : null}
            {mine || canDelete ? <ActionButton icon={Trash2} label={t('common.delete')} onClick={() => onDelete(c)} danger /> : null}
          </div>
        ) : null}
      </div>
    </li>
  );
}
